/// Profile
// props:
// Optional:
// image - (src for image)
// name - (name to be displayed, optional but recommended to set either name or role at least [bold])
// role - (role to be displayed [thin])
// comment - (comment to be displayed [normal])
// content - (content to be displayed in place of text)
// children - (will be placed after elements, used for modals or dropdowns)
// tags - hoverText (hides text unless hovered, adds max height), commentText (aligns content to top)
// style - inherited to wrapper
// edit - edit("edit" / "delete") - adds edit button that runs function

import classes from './Profile.module.scss';
import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { retext } from 'retext';
import emoji from 'retext-emoji';
import Linkify from 'react-linkify';
import { v4 as uuidv4 } from 'uuid';

const emojify = (text) => {
    return retext()
        .use(emoji, { convert: 'encode' })
        .processSync(text).toString()
}

const markDown = (text) => {
    //let wrapBold = (wrap) => (
    //    <b>{wrap}</b>
    //)
    //let wrapItalic = (wrap) => (
    //    <i>{wrap}</i>
    //)
    //let wrapStrikethrough = (wrap) => (
    //    <strike>{wrap}</strike>
    //)
    //let wrapUnderline = (wrap) => (
    //    <u>{wrap}</u>
    //)
    let marks = [
        {
            from: "***",
            to: (wrap, key) => (<b key={key}><i>{wrap}</i></b>),
        }, {
            from: "**",
            to: (wrap, key) => (<b key={key}>{wrap}</b>),
        }, {
            from: "*",
            to: (wrap, key) => (<i key={key}>{wrap}</i>),
        }, {
            from: "~~",
            to: (wrap, key) => (<strike key={key}>{wrap}</strike>),
        }, {
            from: "__",
            to: (wrap, key) => (<u key={key}>{wrap}</u>),
        }
    ]
    let hashes = [];
    let mark = (wrap, marks, index) => {
        let modified = [];
        wrap.forEach((el) => {
            if (typeof (el) === "string") {
                let splittext = el.split(marks[index].from);
                if (splittext.length > 2) {
                    for (var i = 0; i < Math.floor(splittext.length / 3); i++) {
                        hashes.push({
                            key: uuidv4(),
                            function: index
                        });
                        modified.push(splittext[i * 3])
                        modified.push({ marker: hashes[hashes.length - 1] })
                        modified.push(splittext[i * 3 + 1])
                        modified.push({ marker: hashes[hashes.length - 1] })
                        modified.push(splittext[i * 3 + 2])
                    }
                } else {
                    modified.push(el)
                }
            } else {
                modified.push(el)
            }
        })
        if ((index + 1) < marks.length) {
            return mark([...modified], [...marks], index + 1)
        }
        return [...modified];
    }
    let modified = mark([text], [...marks], 0);
    hashes.forEach((el) => {
        let markerIndex = []
        modified.forEach((marker, ind) => {
            if ((typeof (marker) === "object") && marker.marker && (el.key === marker.marker.key)) {
                markerIndex.push(ind)
            }
        })
        if (markerIndex.length === 2) {
            for (var i = markerIndex[0]; i < markerIndex[1]; i++) {
                if (typeof el === "string") {
                    modified[i] = emojify(modified[i])
                }
                if ((typeof modified[i] === "object") && modified[i].marker) {

                } else {
                    modified[i] = marks[el.function].to(modified[i], "marked" + i);
                }
            }
        }
    })
    modified = [...modified.filter((el) => ((typeof (el) !== "object") || el.$$typeof))];
    modified = modified.map((el) => {
        if (typeof el === "string") {
            return emojify(el);
        } else {
            return el;
        }
    })
    return (
        <>
            {modified}
        </>
    )
}
export default function Profile(props) {
    const [commentOpen, setCommentOpen] = useState(false);
    const [showButton, setShowButton] = useState(false);
    const [commentMarkedDown, setCommentMarkedDown] = useState(null);
    const commentOpenRef = useRef(commentOpen);
    const comment = useRef(null);
    let tags = [];
    if (props.tags) tags = [...props.tags]
    let customstyle = {};
    if (tags.includes("commentText")) {
        customstyle = {
            ...customstyle,
            justifyContent: "flex-start"
        }
    }
    useEffect(() => {
        if (comment && comment.current && comment.current.clientHeight < comment.current.scrollHeight) {
            setShowButton(true);
        }
    }, [commentMarkedDown])

    useEffect(() => {
        commentOpenRef.current = commentOpen
    }, [commentOpen])

    // Check comment read more if window is resized
    useLayoutEffect(() => {
        function updateSize() {
            if ((comment && comment.current && comment.current.clientHeight < comment.current.scrollHeight) || commentOpenRef.current) {
                setShowButton(true);
            } else {
                setShowButton(false);
            }
        }
        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {
        if (props.comment) {
            setCommentMarkedDown(markDown(props.comment))
        }
    }, [props.comment])

    let timestampValue = null;
    if (props.timestamp) {
        let seconds = (Date.now() - Date.parse(props.timestamp)) / 1000;
        let minutes = seconds / 60;
        let hours = minutes / 60;
        let days = hours / 24;
        let months = days / 30.4167;
        let years = months / 12;
        if (years > 1) {
            timestampValue = Math.floor(years) + " year" + (Math.floor(years) > 1 ? "s" : "")
        } else if (months > 1) {
            timestampValue = Math.floor(months) + " month" + (Math.floor(months) > 1 ? "s" : "")
        } else if (days > 1) {
            timestampValue = Math.floor(days) + " day" + (Math.floor(days) > 1 ? "s" : "")
        } else if (hours > 1) {
            timestampValue = Math.floor(hours) + " hour" + (Math.floor(hours) > 1 ? "s" : "")
        } else if (minutes > 1) {
            timestampValue = Math.floor(minutes) + " minute" + (Math.floor(minutes) > 1 ? "s" : "")
        } else {
            timestampValue = Math.floor(seconds) + " second" + (Math.floor(seconds) > 1 ? "s" : "")
        }
        timestampValue = timestampValue + " ago";
    }

    let nameElement = null;
    if (props.name) nameElement = (<div className={classes.Name}>{props.name}</div>)

    let nameAndTimestamp = null;
    if (timestampValue) {
        nameAndTimestamp = (
            <div style={{ display: "flex" }}>
                {nameElement}
                <div style={{ display: "flex", alignItems: "center", marginLeft: "15px" }}><div className={classes.TimestampText}>{timestampValue}</div></div>
                {props.edit ? <div style={{ display: "flex", alignItems: "center", marginLeft: "15px" }}><div className={classes.EditButton} onClick={() => { props.edit("edit") }}>Edit</div></div> : null}
                {props.edit ? <div style={{ display: "flex", alignItems: "center", marginLeft: "15px" }}><div className={classes.EditButton} onClick={() => { props.edit("remove") }}>Remove</div></div> : null}
                {props.reply ? <div style={{ display: "flex", alignItems: "center", marginLeft: "15px" }}><div className={classes.EditButton} onClick={() => { props.reply("reply") }}>Reply</div></div> : null}
            </div>
        )
    }

    return (
        <div className={classes.Profile}>
            {props.image ? <img className={classes.Profpic} src={props.image} alt="profile" /> : null}
            <div className={classes.ProftextTransition + ((tags.includes("hoverText")) ? "" : " " + classes.ProftextPreventTransition)}>
                <div className={classes.Proftext} style={customstyle}>
                    {timestampValue ? nameAndTimestamp : nameElement}
                    {props.role ? <div className={classes.Role}>{props.role}</div> : null}
                    {commentMarkedDown ? <div className={classes.CommentWrap}><div className={classes.Comment} style={{ WebkitLineClamp: (commentOpen) ? "1000" : "2" }} ref={comment}><Linkify>{commentMarkedDown}</Linkify></div></div> : null}
                    {props.comment && showButton ? <div className={classes.Comment} style={{ fontWeight: "bold", cursor: "pointer" }} onClick={() => { setCommentOpen(!commentOpen) }}>{commentOpen ? "Show Less" : "Read More"}</div> : null}
                    {props.content ? props.content : null}
                </div>
            </div>
            {props.children}
        </div>
    )
}